// "Promote to in force": picks the day the version comes into force (not before the current in-force version's valid_from), shows which version is superseded then, and promotes it under the document lock through POST /documents/{doc_uid}/promote; when the server refuses a version that leaves out much of the version in force (version-removes-content), it shows what is left out and promotes only with a second confirmation.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlertIcon } from "lucide-react";
import { useState } from "react";

import type { DocumentOut } from "@/api/__generated__";
import {
  lifecycleListFamilyVersionsOptions,
  lifecyclePromoteDocumentMutation,
} from "@/api/__generated__/@tanstack/react-query.gen";
import { invalidateGraphData } from "@/api/keys";
import { isApiProblem } from "@/api/problem";
import { Mono } from "@/components/common/Mono";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { notify } from "@/components/ui/sonner";
import { CheckboxField } from "@/features/jobs/dialogs/CheckboxField";
import { DateField, todayIso } from "@/features/jobs/dialogs/DateField";
import { JobDialogFrame, JobDialogLayout } from "@/features/jobs/dialogs/JobDialog";
import { formatDate } from "@/lib/format";

interface PromoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docUid: string;
  familyId: string;
}

export function PromoteDialog({ open, onOpenChange, docUid, familyId }: PromoteDialogProps) {
  return (
    <JobDialogFrame open={open} onOpenChange={onOpenChange} size="sm">
      <PromoteForm
        docUid={docUid}
        familyId={familyId}
        onDone={() => {
          onOpenChange(false);
        }}
      />
    </JobDialogFrame>
  );
}

export function currentInForce(
  versions: readonly DocumentOut[] | undefined,
  docUid: string,
): DocumentOut | null {
  return versions?.find((version) => version.status === "in_force" && version.doc_uid !== docUid) ?? null;
}

export function removalsRefused(error: unknown): string | null {
  if (!isApiProblem(error) || error.code !== "version-removes-content") {
    return null;
  }
  return error.detail ?? error.title;
}

export function promoteDateError(validFrom: string, current: DocumentOut | null): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(validFrom)) {
    return "Enter the day the version comes into force.";
  }
  if (current?.valid_from && validFrom < current.valid_from) {
    return `Choose ${formatDate(current.valid_from)} or later, when ${current.doc_uid} came into force.`;
  }
  return null;
}

function PromoteForm({ docUid, familyId, onDone }: { docUid: string; familyId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const versions = useQuery(lifecycleListFamilyVersionsOptions({ path: { family_id: familyId } }));
  const current = currentInForce(versions.data, docUid);
  const [validFrom, setValidFrom] = useState(todayIso);
  const error = promoteDateError(validFrom, current);
  const [removals, setRemovals] = useState<string | null>(null);
  const [confirmRemovals, setConfirmRemovals] = useState(false);
  const promote = useMutation({
    ...lifecyclePromoteDocumentMutation(),
    onError: (refusal) => {
      setRemovals((shown) => removalsRefused(refusal) ?? shown);
    },
    onSuccess: (result) => {
      void invalidateGraphData(queryClient);
      notify(
        `${result.doc_uid} is in force`,
        result.superseded_doc_uid === null ? undefined : `${result.superseded_doc_uid} is superseded.`,
      );
      onDone();
    },
  });
  return (
    <JobDialogLayout
      title="Promote to in force"
      description={
        <>
          Puts <Mono tone="ink">{docUid}</Mono> in force from the chosen day.
        </>
      }
      confirmLabel={removals !== null && confirmRemovals ? "Promote anyway" : "Promote"}
      canConfirm={error === null && !versions.isPending && (removals === null || confirmRemovals)}
      pending={promote.isPending}
      error={removalsRefused(promote.error) === null ? promote.error : undefined}
      note="Runs under the document lock."
      onConfirm={() => {
        promote.mutate({
          path: { doc_uid: docUid },
          body:
            removals !== null && confirmRemovals
              ? { valid_from: validFrom, confirm_removals: true }
              : { valid_from: validFrom },
        });
      }}
    >
      <DateField
        label="In force from"
        value={validFrom}
        onChange={setValidFrom}
        min={current?.valid_from ?? undefined}
        required
        error={error}
        description={
          current === null
            ? "No other version of this family is in force."
            : `${current.doc_uid} becomes superseded on this day.`
        }
      />
      {removals === null ? null : (
        <>
          <Alert variant="attn" role="alert">
            <TriangleAlertIcon aria-hidden />
            <AlertTitle>This version leaves out part of the version in force</AlertTitle>
            <AlertDescription>
              <p>{removals}</p>
            </AlertDescription>
          </Alert>
          <CheckboxField
            checked={confirmRemovals}
            onCheckedChange={setConfirmRemovals}
            label="Promote it anyway"
            description="Only when these clauses and sections were really removed from the grid code. Otherwise, build the version from the complete PDF."
          />
        </>
      )}
    </JobDialogLayout>
  );
}
